/**
 * Background music — a looping playlist player over vendored OGG tracks. HTMLAudio, no
 * external dep (same spirit as Sound.ts). The caller supplies the track list (see
 * src/assets/music/tracks.ts); this class just plays it: a multi-track list chains on
 * 'ended' and wraps to the top, a single-track list uses native gapless looping.
 *
 * This is background flavour, NOT part of the deterministic sim — but it lives under
 * src/engine, so it must stay Math.random-free (the determinism lint). Any RANDOM track
 * ORDER is the caller's job (tracks.ts `shuffled()`, outside src/engine); the player only
 * ever walks the list it was handed, in order.
 *
 * Browsers gate autoplay until a user gesture, so start() tries immediately and, if
 * blocked, arms a one-shot pointer/key listener that kicks playback on the first
 * interaction (in-game that's the first movement key). Volume + mute apply live.
 *
 * Volume/mute are persisted to localStorage (POLISH_07 #4) — every level load is a full
 * page navigation (main.ts advanceCurated → navigate()), so without persistence a pause-menu
 * volume/mute choice reset on the very next level. Music is explicitly OUTSIDE the
 * deterministic sim (see above), so localStorage here is fine — same discretion the
 * reduced-motion pref uses (main.ts maker.reduceFx.v1).
 */
const VOL_KEY = 'maker.musicVol.v1';
const MUTE_KEY = 'maker.musicMute.v1';

/**
 * Pure parse/clamp of a raw localStorage string into a volume — no DOM access, so this is
 * unit-testable without jsdom (Music.test.ts). `null` (key absent) or a non-finite/garbage
 * value falls back to `fallback`; an in-range numeric string clamps to [0,1].
 */
export function parseStoredVolume(raw: string | null, fallback: number): number {
  if (raw === null) return fallback;
  const v = Number(raw);
  if (!Number.isFinite(v)) return fallback;
  return Math.min(1, Math.max(0, v));
}
/** Pure parse of a raw localStorage mute flag ('1' = muted); anything else falls back. */
export function parseStoredMuted(raw: string | null, fallback: boolean): boolean {
  if (raw === null) return fallback;
  return raw === '1';
}

function readStoredVolume(fallback: number): number {
  try { return parseStoredVolume(localStorage.getItem(VOL_KEY), fallback); } catch { return fallback; }
}
function readStoredMuted(fallback: boolean): boolean {
  try { return parseStoredMuted(localStorage.getItem(MUTE_KEY), fallback); } catch { return fallback; }
}
function writeStoredVolume(v: number): void {
  try { localStorage.setItem(VOL_KEY, String(v)); } catch { /* private mode */ }
}
function writeStoredMuted(m: boolean): void {
  try { localStorage.setItem(MUTE_KEY, m ? '1' : '0'); } catch { /* private mode */ }
}

export class Music {
  private tracks: readonly string[];
  private audio = new Audio();
  private i = 0;
  private muted = readStoredMuted(false);
  private volume = readStoredVolume(0.4); // sits under the 0.5 SFX so cues stay audible
  private started = false;
  private armed = false;
  private readonly onEnded = (): void => this.next();
  private readonly onGesture = (): void => this.kick();

  /** @param tracks one or more resolved track URLs. A single-track list loops natively. */
  constructor(tracks: readonly string[]) {
    this.tracks = tracks.length ? tracks : [''];
    this.audio.preload = 'auto';
    // Single track → gapless native loop; multi-track → chain on 'ended' (loop off).
    this.audio.loop = this.tracks.length === 1;
    this.audio.addEventListener('ended', this.onEnded);
  }

  /** Begin playback — immediately if allowed, else on the first user gesture. */
  start(): void {
    if (this.started) return;
    void this.playCurrent().catch(() => this.arm());
  }

  private arm(): void {
    if (this.armed) return;
    this.armed = true;
    window.addEventListener('pointerdown', this.onGesture, { once: true });
    window.addEventListener('keydown', this.onGesture, { once: true });
  }
  private kick(): void { this.armed = false; void this.playCurrent().catch(() => {}); }

  private async playCurrent(): Promise<void> {
    if (!this.tracks[this.i]) return;
    this.audio.src = this.tracks[this.i];
    this.audio.volume = this.muted ? 0 : this.volume;
    await this.audio.play();
    this.started = true;
  }

  private next(): void {
    this.i = (this.i + 1) % this.tracks.length;
    void this.playCurrent().catch(() => {});
  }

  setMuted(m: boolean): void { this.muted = m; this.audio.volume = m ? 0 : this.volume; writeStoredMuted(m); }
  setVolume(v: number): void { this.volume = v; if (!this.muted) this.audio.volume = v; writeStoredVolume(v); }
  getMuted(): boolean { return this.muted; }
  getVolume(): number { return this.volume; }

  /** Pause (keeps the 'ended' chain bound so a later start() resumes the playlist). */
  stop(): void {
    this.audio.pause();
    window.removeEventListener('pointerdown', this.onGesture);
    window.removeEventListener('keydown', this.onGesture);
    this.armed = false;
    this.started = false;
  }
}
